import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AlertService } from '@app/services';

import { MTranslateService } from '@mercadona/core/translate';

import { genericErrors } from '@/entities/constants/handler-error.model';
import { UNLIMITED_TIME } from '@/entities/constants/ptrab/constants';
import { IRPF_ERRORS } from '@/entities/constants/ptrab/irpf.const';
import { ErrorMsgEnum } from '@/enums/error/error-msg-enum';
import { Permissions } from '@/enums/permissions/permissions.enum';
import { TwoFactorError } from '@/interfaces/ptrab/two-factor-authorization.interface';
import { ErrorMessages } from '@/models/error/error.model';

@Injectable({
  providedIn: 'root'
})
export class SessionErrorHandler {
  private readonly MESSAGES = {
    GENERIC: 'COMMON.ERROR',
    UNKNOWN: 'EMPLOYEE_PORTAL.UNKNOWN_EXTERNAL_ERROR'
  };

  private readonly codeExceptions = [
    ErrorMessages.PTRAB_UNAUTHORIZED,
    ErrorMessages.PTRAB_BLOCKED,
    ErrorMessages.INCORRECT_PASSWORD,
    ErrorMessages.RISKY_PREGNANCY,
    ErrorMessages.EMPTY_GARNISHMENT,
    ErrorMessages.TOKEN_EXPIRED,
    ErrorMessages.USER_BANNED,
    ErrorMessages.RESOURCE_LOCKED,
    ErrorMessages.EMPTY_PAYSLIPS,
    ErrorMessages.PERM_DEACTIVATED,
    ErrorMessages.INVALID_IBAN,
    ErrorMessages.EXTERNAL_CLIENT_UNAVAILABLE,
    ErrorMessages.EMPTY_SLOTS,
    TwoFactorError.VALIDATION_CODE_INVALID,
    TwoFactorError.VALIDATION_CODE_EXPIRED,
    TwoFactorError.VALIDATION_CODE_MAX_TRIES_EXCEEDED,
    TwoFactorError.VALIDATION_CODE_USER_BLOCKED,
    ErrorMsgEnum.SERVICE_LOCKED,
    ErrorMsgEnum.SERVER_ERROR,
    ...Object.keys(genericErrors)
  ];

  private showToastErrors = true;

  constructor(
    private translateService: MTranslateService,
    private alertService: AlertService
  ) {}

  handleExternalError(response: HttpErrorResponse) {
    if (!response.error || !this.showToastErrors) {
      return;
    }

    const { code, message } = response.error;

    if (this.isCodeException(response.status, code, response.error.sections)) {
      return;
    }

    const translations = [
      this.translateService.translate(this.MESSAGES.GENERIC),
      `EMPLOYEE_PORTAL.${code}`,
      this.MESSAGES.UNKNOWN
    ];

    this.showError(translations, `EMPLOYEE_PORTAL.${code}`, message);
  }

  enableToast() {
    this.showToastErrors = true;
  }

  private isCodeException(status: number, code: string, sections: Permissions[]) {
    // Evitamos que salga el alert-error cuando se entra con usuario de TF
    const percentageSections: Permissions[] = [
      Permissions.PERCENTAGE_READJUSTMENT,
      Permissions.IRPF_PERCENTAGE_READJUSTMENT
    ];
    const hasPercentageSection = sections?.some((s) => percentageSections.includes(s));
    const isNavarraErrorDenied = status === 403 && code === 'ERR.PERM.DENIED' && hasPercentageSection;

    return (
      isNavarraErrorDenied ||
      !code ||
      this.codeExceptions.find((errorCode) => code === errorCode) ||
      IRPF_ERRORS.find((errorCode) => code === errorCode)
    );
  }

  private showError(translations: string[], code: string, text: string) {
    if (translations[1] === this.translateService.translate(code)) {
      this.alertService.showError(translations[0], text, UNLIMITED_TIME);
      return;
    }

    this.alertService.showError(translations[0], translations[1]);
  }
}
